import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { FAQS, ATTORNEY } from "@/data/content";

const SITE_URL = "https://joehelfrickinjuryattorney.com";

export default function FAQ() {
    // QAPage schema — Google surfaces this differently than FAQPage
    // (per-question rich result). Emitted here in addition to the
    // FAQPage schema that SEO.jsx generates from the `faqs` prop.
    const qaSchema = {
        "@context": "https://schema.org",
        "@type": "QAPage",
        "@id": `${SITE_URL}/faq#qapage`,
        mainEntity: FAQS.map((f, i) => ({
            "@type": "Question",
            "@id": `${SITE_URL}/faq#q${i + 1}`,
            name: f.q,
            answerCount: 1,
            upvoteCount: 0,
            author: { "@id": `${SITE_URL}/#person` },
            acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
                dateCreated: "2025-12-15T08:00:00-08:00",
                upvoteCount: 0,
                url: `${SITE_URL}/faq#q${i + 1}`,
                author: { "@id": `${SITE_URL}/#person` },
            },
        })),
    };

    return (
        <>
            <SEO
                title="FAQ — Sacramento Personal Injury Questions Answered"
                description="Straight answers to the most common questions about hiring a personal injury attorney in Sacramento, CA, and surrounding areas, California — costs, deadlines, damages, dog bites, government claims, and what to do after an accident."
                path="/faq"
                schema={qaSchema}
                faqs={FAQS}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "FAQ", path: "/faq" },
                ]}
            />

            {/* HERO */}
            <section data-testid="faq-hero" className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            FAQ
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            Straight answers,{" "}
                            <span className="italic text-[#D4AF37]">
                                no legalese.
                            </span>
                        </h1>
                        <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                            The questions Joe Helfrick gets every week from
                            people just hurt in Sacramento County or Placer
                            County — answered the way he'd answer them at the
                            kitchen table.
                        </p>
                        <div
                            className="tldr mt-8 max-w-3xl border-l-2 border-[#D4AF37] pl-5 text-[#F7F5EB]/85 text-sm leading-relaxed"
                            data-speakable
                        >
                            <span className="block text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-2">
                                Quick Answer
                            </span>
                            Joe Helfrick is a Sacramento-born personal injury
                            attorney serving Sacramento, CA, and surrounding areas. All
                            consultations are free and confidential, all cases
                            are taken on contingency (no fee unless we win), and
                            California gives you only two years to file most
                            injury lawsuits — much less for medical malpractice
                            or claims against a government entity.
                        </div>
                    </div>
                </div>
            </section>

            {/* QUESTIONS */}
            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="divide-y divide-[#1F3458]">
                        {FAQS.map((f, i) => (
                            <details
                                key={f.q}
                                data-testid={`faq-item-${i}`}
                                className="group py-7"
                            >
                                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                                    <div className="flex items-start gap-4">
                                        <HelpCircle
                                            size={20}
                                            className="text-[#D4AF37] shrink-0 mt-1"
                                        />
                                        <span className="font-serif text-xl md:text-2xl text-[#F7F5EB] leading-snug">
                                            {f.q}
                                        </span>
                                    </div>
                                    <span className="text-[#D4AF37] text-2xl transition-transform group-open:rotate-45 leading-none">
                                        +
                                    </span>
                                </summary>
                                <p className="mt-4 ml-9 text-[#9FB3D4] leading-relaxed text-base">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section data-testid="faq-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-5xl text-[#F7F5EB] leading-tight">
                            Have a question{" "}
                            <span className="italic text-[#D4AF37]">
                                not answered here?
                            </span>
                        </h2>
                        <p className="mt-5 text-[#9FB3D4] text-lg max-w-xl">
                            Send Joe a quick note or call directly. There's no
                            cost to ask.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-wrap gap-4">
                        <Link
                            to="/contact"
                            data-testid="faq-cta-contact"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Ask Joe <ArrowRight size={16} />
                        </Link>
                        <a
                            href={`tel:${ATTORNEY.phone.replace(/[^0-9]/g, "")}`}
                            data-testid="faq-cta-phone"
                            className="inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#D4AF37] hover:text-[#07142B] transition-colors"
                        >
                            Call {ATTORNEY.phone}
                        </a>
                        <Link
                            to="/case-evaluator"
                            data-testid="faq-cta-evaluator"
                            className="inline-flex items-center gap-3 text-[#9FB3D4] font-bold uppercase tracking-wider text-sm px-2 py-4 hover:text-[#D4AF37] transition-colors"
                        >
                            60-Second Case Evaluator <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
